import { ReactNode, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'

import { useOpenState } from '@hooks/open-state'
import { Modal } from './modal'

export function SeeMore({
  initiallyOpen = false,
  children,
  className,
  clamp = 2,
  lineHeight = 20,
  inModal = false,
}: {
  initiallyOpen?: boolean
  children: ReactNode
  className?: string
  clamp?: 1 | 2 | 3 | 4 | 5 | 6
  lineHeight?: number
  inModal?: boolean
}) {
  const [showToggle, setShowToggle] = useState<boolean>(false)
  const [expanded, setExpanded] = useState(initiallyOpen)
  const [clamped, setClamped] = useState(!expanded)
  const [hiddenContent, setHiddenContent] = useState<HTMLDivElement | null>()
  const [mainContent, setMainContent] = useState<HTMLDivElement | null>()
  const seeMoreModal = useOpenState()

  const toggle = useCallback(() => {
    // Toggle should just open the detail modal if specified by props.
    // Only called to open, because the modal handles closing on its own
    if (inModal) {
      seeMoreModal.open()
      return
    }

    // Otherwise toggle the actual max-height and line clamp settings based on the current state
    if (!expanded) {
      setClamped(false)
      setExpanded(true)
    } else {
      setExpanded(false)
      setTimeout(() => setClamped(true), 200)
    }
  }, [expanded, seeMoreModal, inModal])

  useEffect(() => {
    if (hiddenContent && mainContent) {
      const mainContentHeight = mainContent?.offsetHeight ?? 0
      const hiddenContentHeight = hiddenContent?.offsetHeight ?? 0
      setShowToggle(hiddenContentHeight > mainContentHeight)
    }
  }, [hiddenContent, mainContent])

  return (
    <div className={classNames(className, 'w-full')}>
      <div className="relative">
        <div className="relative overflow-hidden">
          <div ref={setHiddenContent} className="absolute inset-x-0 opacity-0">
            {children}
          </div>
          <div
            ref={setMainContent}
            style={{ maxHeight: expanded ? hiddenContent?.offsetHeight : clamp * lineHeight }}
            className={classNames(
              className,
              'see-more duration-200 ease-out transition-[max-height] will-change-[max-height]',
              clamped
                ? {
                    'line-clamp-1': clamp === 1,
                    'line-clamp-2': clamp === 2,
                    'line-clamp-3': clamp === 3,
                    'line-clamp-4': clamp === 4,
                    'line-clamp-5': clamp === 5,
                    'line-clamp-6': clamp === 6,
                  }
                : undefined
            )}
          >
            {children}
          </div>
        </div>
        {showToggle && (
          <div className="absolute right-0 -bottom-4">
            <span onClick={toggle} className="text-sm link">
              {expanded ? 'Show less' : 'Show more'}
            </span>
          </div>
        )}
      </div>
      {inModal && (
        <Modal {...seeMoreModal} title="More Details">
          {children}
        </Modal>
      )}
    </div>
  )
}
